import type { Channel } from '@/bindings/Channel'
import type { LoginIdentifier } from '@/bindings/LoginIdentifier'
import { isSupportedCountry } from 'libphonenumber-js'

import { defaultNation } from '@/lib/nations'

/** Both channels' text lives here, so toggling never loses what was typed. */
export interface IdentifierDraft {
  channel: Channel
  nation: string
  number: string
  email: string
}

export function emptyIdentifierDraft(channel: Channel = 'email'): IdentifierDraft {
  return { channel, nation: readRememberedNation() ?? defaultNation(), number: '', email: '' }
}

const CHANNEL_KEY = 'auth.channel'
const NATION_KEY = 'auth.nation'

/** The channel of the last successful login on this device, if any. */
export function readRememberedChannel(): Channel | undefined {
  try {
    const channel = localStorage.getItem(CHANNEL_KEY)
    return channel === 'phone' || channel === 'email' ? channel : undefined
  } catch {
    return undefined
  }
}

/** The nation of the last successful phone login on this device, if it still has a numbering plan. */
function readRememberedNation(): string | undefined {
  try {
    const nation = localStorage.getItem(NATION_KEY)
    return nation && isSupportedCountry(nation) ? nation : undefined
  } catch {
    return undefined
  }
}

/** Remember the channel, and the nation when it was a phone login, for the next visit. */
export function rememberIdentifier(draft: IdentifierDraft) {
  try {
    localStorage.setItem(CHANNEL_KEY, draft.channel)
    if (draft.channel === 'phone') localStorage.setItem(NATION_KEY, draft.nation)
  } catch {
    // Storage unavailable: the next login just opens on email with the guessed nation.
  }
}

/** True when the active channel has any input. */
export function identifierTyped(draft: IdentifierDraft): boolean {
  return (draft.channel === 'phone' ? draft.number : draft.email).trim() !== ''
}

/** True when either channel has input: the inactive one is kept, so it still counts. */
export function anyIdentifierTyped(draft: IdentifierDraft): boolean {
  return draft.number.trim() !== '' || draft.email.trim() !== ''
}

/** True when the text changed, not just the channel, so a lookup result no longer applies. */
export function identifierEdited(before: IdentifierDraft, after: IdentifierDraft): boolean {
  return before.number !== after.number || before.email !== after.email || before.nation !== after.nation
}

export function toLoginIdentifier(draft: IdentifierDraft): LoginIdentifier {
  return draft.channel === 'phone'
    ? { type: 'phone', nation: draft.nation, number: draft.number.trim() }
    : { type: 'email', value: draft.email.trim() }
}
